import { isCallable } from '@afterpay/utils'
import clsx from 'clsx'
import React, { useContext } from 'react'

import { Text } from '../Typography'
import { noop } from '../utils/function'
import { KeyCodes } from '../utils/keyboard'
import { TAB_DISPLAY_NAME } from './constants'
import styles from './style.module.scss'
import { TabContext } from './TabContext'
import { ValueType } from './types'

export interface TabProps extends React.PropsWithRef<JSX.IntrinsicElements['li']> {
  value: ValueType
  'aria-controls'?: string
  testNameSpace?: string
}

export const Tab = React.forwardRef<HTMLLIElement, TabProps>(
  ({ value, children, onClick = noop, onKeyDown, 'aria-controls': ariaControls, testNameSpace = '' }, ref): JSX.Element => {
    const tabContext = useContext(TabContext)
    const isActive = tabContext.value === value

    const itemClassNames = clsx(
      styles.tabsItem,
      'size' in tabContext && styles[`tabsItem${tabContext.size}`],
      'kind' in tabContext && styles[`tabsItem${tabContext.kind}`],
      'color' in tabContext && styles[`tabsItem${tabContext.color}`],
      tabContext.stretch && styles.tabsItemStretch,
      (tabContext.animation ?? false) && styles.isAnimated,
      isActive && styles.isActive
    )

    const handleClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
      if (isCallable(tabContext.onChange)) {
        tabContext.onChange(value)
      }
      onClick(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>): void => {
      if (isCallable(onKeyDown)) {
        onKeyDown(e)
      }
      if (e.defaultPrevented) {
        return
      }

      if (e.key === KeyCodes.Enter || e.key === KeyCodes.Space) {
        e.preventDefault()
        if (isCallable(tabContext.onChange)) {
          tabContext.onChange(value)
        }
      }
    }

    return (
      <li
        tabIndex={isActive ? 0 : -1}
        role='tab'
        ref={ref}
        className={itemClassNames}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-tab-item` })}
        {...(isActive && { 'aria-selected': true, 'aria-controls': ariaControls })}
      >
        <Text bold={isActive}>{children}</Text>
      </li>
    )
  }
)

Tab.displayName = TAB_DISPLAY_NAME
